/**
 * Request Queue Manager
 *
 * Handles request deduplication, priority queuing, and cancellation.
 */

// Request priority levels
export enum RequestPriority {
  CRITICAL = 0,  // Auth, security-related
  HIGH = 1,      // User-initiated actions
  NORMAL = 2,    // Data fetching
  LOW = 3,       // Background sync, analytics
}

// Request metadata
export interface RequestMeta {
  id: string;
  endpoint: string;
  priority: RequestPriority;
  timestamp: number;
  abortController?: AbortController;
  retries: number;
  maxRetries: number;
}

// Request queue manager
export class RequestQueueManager {
  private queues: Map<RequestPriority, RequestMeta[]> = new Map([
    [RequestPriority.CRITICAL, []],
    [RequestPriority.HIGH, []],
    [RequestPriority.NORMAL, []],
    [RequestPriority.LOW, []],
  ]);

  private activeRequests: Map<string, RequestMeta> = new Map();
  private dedupCache: Map<string, Promise<unknown>> = new Map();
  private maxConcurrent = 6;
  private processing = false;

  /**
   * Add request to queue
   */
  enqueue(request: RequestMeta): void {
    const queue = this.queues.get(request.priority) || [];
    queue.push(request);
    this.processQueue();
  }

  /**
   * Process queued requests
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.activeRequests.size >= this.maxConcurrent) {
      return;
    }

    this.processing = true;

    // Process by priority
    for (const [, queue] of this.queues) {
      while (queue.length > 0 && this.activeRequests.size < this.maxConcurrent) {
        const request = queue.shift();
        if (request) {
          this.activeRequests.set(request.id, request);
          // Request would be executed here
        }
      }
    }

    this.processing = false;
  }

  /**
   * Cancel request
   */
  cancel(requestId: string): void {
    const request = this.activeRequests.get(requestId);
    if (request?.abortController) {
      request.abortController.abort();
      this.activeRequests.delete(requestId);
    }

    // Remove from queues
    for (const queue of this.queues.values()) {
      const index = queue.findIndex(r => r.id === requestId);
      if (index !== -1) {
        queue.splice(index, 1);
      }
    }
  }

  /**
   * Cancel all requests for an endpoint
   */
  cancelEndpoint(endpoint: string): void {
    // Cancel active requests
    for (const [id, request] of this.activeRequests) {
      if (request.endpoint === endpoint) {
        this.cancel(id);
      }
    }

    // Remove from queues
    for (const queue of this.queues.values()) {
      queue.splice(0, queue.length, ...queue.filter(r => r.endpoint !== endpoint));
    }
  }

  /**
   * Get deduplication key
   */
  private getDedupKey(endpoint: string, params?: unknown): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }

  /**
   * Check for duplicate request
   */
  checkDuplicate(endpoint: string, params?: unknown): Promise<unknown> | null {
    const key = this.getDedupKey(endpoint, params);
    return this.dedupCache.get(key) || null;
  }

  /**
   * Store request for deduplication
   */
  storeDuplicate(endpoint: string, params: unknown, promise: Promise<unknown>): void {
    const key = this.getDedupKey(endpoint, params);
    this.dedupCache.set(key, promise);

    // Clean up after completion
    promise.finally(() => {
      setTimeout(() => this.dedupCache.delete(key), 100);
    });
  }

  /**
   * Get queue statistics
   */
  getStats() {
    return {
      active: this.activeRequests.size,
      queued: Array.from(this.queues.values()).reduce((sum, queue) => sum + queue.length, 0),
      cached: this.dedupCache.size,
    };
  }
}
